<template>
  <v-dialog v-model="isVisible" max-width="1000px" persistent>
    <v-card>
      <v-card-title class="popup-header">
        <div class="header-content">
          <div class="header-title">
            <v-icon left size="24">mdi-calendar-edit</v-icon>
            <span class="headline white-text">
              {{ isAdmin ? 'Gestionar Fichajes' : 'Ver Fichajes' }}
            </span>
          </div>
          <div class="header-date">
            {{ formatDateShort(selectedDate) }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon @click="closePopup" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="popup-content">
        <!-- Sección de Ausencias -->
        <div v-if="hasAbsences" class="absences-section">
          <div class="section-header">
            <v-icon color="primary" left>mdi-calendar-check</v-icon>
            <span class="section-title">Ausencias</span>
          </div>

          <div
            v-for="absence in nonHolidayAbsences"
            :key="absence.uuid"
            class="absence-card"
          >
            <v-card outlined :class="getAbsenceCardClass(absence.status)">
              <v-card-text class="absence-card-content">
                <v-row align="start">
                  <v-col cols="12" md="8">
                    <div class="absence-info">
                      <div v-if="absence.lista_motivos" class="absence-detail">
                        <strong>Tipo:</strong>
                        <span>{{ absence.lista_motivos }}</span>
                      </div>
                      <div class="absence-detail">
                        <strong>Motivo:</strong>
                        <span>{{ absence.reason || 'Sin especificar' }}</span>
                      </div>
                      <div class="absence-detail">
                        <strong>Horario:</strong>
                        <span>{{ formatAbsenceTime(absence.start) }} - {{ formatAbsenceTime(absence.end) }}</span>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4" class="absence-actions-col">
                    <div class="absence-actions-wrapper">
                      <v-btn
                        v-if="absence.fotoJustificante"
                        outlined
                        small
                        color="#7339B7"
                        @click="openJustificante(absence.fotoJustificante)"
                        title="Ver justificante"
                        class="mb-2"
                      >
                        <v-icon left small>mdi-paperclip</v-icon>
                        Ver Justificante
                      </v-btn>

                      <div v-if="isAdmin && absence.status === 'pending'" class="admin-action-buttons">
                        <v-btn
                          color="success"
                          depressed
                          small
                          @click="approveAbsence(absence)"
                          class="action-btn"
                        >
                          <v-icon left small>mdi-check</v-icon>
                          Aprobar
                        </v-btn>
                        <v-btn
                          color="error"
                          depressed
                          small
                          @click="rejectAbsence(absence)"
                          class="action-btn"
                        >
                          <v-icon left small>mdi-close</v-icon>
                          Rechazar
                        </v-btn>
                      </div>

                      <v-chip
                        v-else-if="absence.status === 'approved'"
                        color="green"
                        text-color="white"
                        class="status-chip"
                      >
                        <v-icon left small>mdi-check-circle</v-icon>
                        Aprobada
                      </v-chip>
                      <v-chip
                        v-else-if="absence.status === 'rejected'"
                        color="red"
                        text-color="white"
                        class="status-chip"
                      >
                        <v-icon left small>mdi-close-circle</v-icon>
                        Rechazada
                      </v-chip>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>

          <v-divider class="my-4"></v-divider>
        </div>

        <!-- Sección de Fichajes -->
        <div class="clocks-section">
          <div class="section-header" v-if="hasAbsences">
            <v-icon color="primary" left>mdi-clock-outline</v-icon>
            <span class="section-title">Fichajes</span>
          </div>

          <div v-if="dayClocks.length === 0" class="no-clocks-message">
            <v-icon color="grey" size="48">mdi-clock-outline</v-icon>
            <p>No hay fichajes para este día</p>
            <v-btn v-if="isAdmin" color="primary" @click="addNewClock">
              <v-icon left>mdi-plus</v-icon>
              Añadir Fichaje
            </v-btn>
          </div>

          <div v-else>
          <div 
            v-for="(clock, index) in dayClocks" 
            :key="clock.uuid || index"
            class="clock-item"
          >
            <v-card outlined class="clock-card" :class="getClockCardClass(clock)">
              <v-card-text>
                <v-row class="clock-fields">
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="clock.type"
                      :items="clockTypes"
                      label="Tipo"
                      outlined
                      :readonly="!isAdmin || clock.typeDisabled"
                      @change="markAsModified(clock)"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="clock.timeString"
                      label="Hora"
                      type="time"
                      outlined
                      :readonly="!isAdmin"
                      @input="updateClockTime(clock)"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row class="justification-row">
                  <v-col cols="12">
                    <v-textarea
                      v-model="clock.justification"
                      label="Justificación"
                      :placeholder="isAdmin ? 'Motivo del cambio (opcional)' : 'Sin justificación'"
                      outlined
                      rows="2"
                      :readonly="!isAdmin"
                      @input="markAsModified(clock)"
                    ></v-textarea>
                  </v-col>
                </v-row>

                <v-row v-if="clock.changes && clock.changes.length > 1">
                  <v-col cols="12">
                    <v-expansion-panels flat>
                      <v-expansion-panel>
                        <v-expansion-panel-header>
                          <span class="changes-header">
                            <v-icon left>mdi-history</v-icon>
                            Historial de cambios ({{ clock.changes.length - 1 }} modificaciones)
                          </span>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <div v-for="(change, changeIndex) in clock.changes.slice(0)" :key="changeIndex" class="change-item">
                            <v-card outlined class="change-card" :class="getChangeCardClass(change.state)">
                              <v-card-text class="py-2">
                                <div class="change-info">
                                  <div class="change-header-row">
                                    <div class="change-time-type">
                                      <strong>{{ formatTime(change.timestamp) }}</strong>
                                      <span class="mx-2">-</span>
                                      <span>{{ change.type === 'in' ? 'Entrada' : 'Salida' }}</span>
                                    </div>
                                    <v-chip
                                      :color="getStateColor(change.state)"
                                      text-color="white"
                                      class="status-chip-large"
                                    >
                                      <v-icon left small>{{ getStateIcon(change.state) }}</v-icon>
                                      {{ getStateText(change.state) }}
                                    </v-chip>
                                  </div>

                                  <div v-if="change.justification" class="change-justification">
                                    <strong>Justificación:</strong> {{ change.justification }}
                                  </div>

                                  <!-- Información de aprobación -->
                                  <div v-if="change.adminApprover || change.admin_uuid" class="approval-info">
                                    <v-icon small color="green" class="mr-1">mdi-check-circle</v-icon>
                                    <strong>Aprobado por:</strong>
                                    <span class="ml-1">
                                      {{ change.adminApprover ? change.adminApprover.username : getAdminName(change.admin_uuid) }}
                                    </span>
                                    <small class="ml-2 text--secondary">
                                      {{ change.reviewedAt ? formatDateTime(change.reviewedAt) : (change.fecha_aprobacion ? formatDateTime(change.fecha_aprobacion) : '') }}
                                    </small>
                                  </div>
                                </div>

                                <div v-if="isAdmin && (change.state === 'pending' || change.state === 'pendiente')" class="admin-actions">
                                  <v-btn
                                    color="success"
                                    depressed
                                    @click="approveChange(clock, change, changeIndex)"
                                    :loading="approvingChange"
                                    class="approve-change-btn"
                                  >
                                    <v-icon left>mdi-check-circle</v-icon>
                                    Aprobar Modificación
                                  </v-btn>
                                </div>
                              </v-card-text>
                            </v-card>
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-col>
                </v-row>

                <div class="clock-actions">
                  <div class="left-actions">
                    <v-chip 
                      v-if="clock.isModified && isAdmin" 
                      color="orange" 
                      text-color="white" 
                      small
                    >
                      Modificado
                    </v-chip>
                    <v-chip 
                      v-if="hasPendingChanges(clock)" 
                      color="purple" 
                      text-color="white" 
                      small
                    >
                      Cambios pendientes
                    </v-chip>
                  </div>
                  <v-spacer></v-spacer>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <div v-if="isAdmin" class="add-clock-section">
            <v-btn 
              color="primary" 
              outlined 
              block 
              @click="addNewClock"
              class="add-clock-btn"
            >
              <v-icon left>mdi-plus</v-icon>
              Añadir Otro Fichaje
            </v-btn>
          </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="popup-actions">
        <v-btn color="grey" text @click="closePopup">
          {{ isAdmin ? 'Cancelar' : 'Cerrar' }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="isAdmin"
          color="primary"
          @click="saveChanges"
          :loading="isSaving"
          :disabled="!hasChanges"
        >
          Guardar Cambios
        </v-btn>
      </v-card-actions>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snackbar.visible"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
      >
        <b>{{ snackbar.text }}</b>
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import { APIClockRepo } from "@/modules/working_hours/infra/APIClockRepository";
import { VueAdminPopup } from "@/modules/working_hours/infra/VueAdminPopup";
import { hasPendingChanges } from "@/modules/working_hours/domain/Utils";
import { PermissionsService } from '../domain/PermissionsService';

export default {
  name: "ClockEditPopup",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    selectedDate: {
      type: String,
      default: null
    },
    clocks: {
      type: Array,
      default: () => []
    },
    absences: {
      type: Array,
      default: () => []
    },
    currentUser: {
      type: Object,
      required: false,
      default: null
    },
    selectedUserId: {
      type: Number,
      default: null
    },
    users: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isVisible: false,
      dayClocks: [],
      clockTypes: [
        { text: 'Entrada', value: 'in' },
        { text: 'Salida', value: 'out' }
      ],
      isSaving: false,
      approvingChange: false,
      adminPopup: null, // Instancia de VueAdminPopup
      // Snackbar
      snackbar: {
        visible: false,
        text: '',
        color: 'error',
        timeout: 3000
      }
    };
  },
  created() {
    // Inicializar VueAdminPopup con el repositorio
    const clockRepo = new APIClockRepo();
    this.adminPopup = new VueAdminPopup(clockRepo);
  },
  computed: {
    isAdmin() {
      // Usar el PermissionsService centralizado que maneja tanto user_type como user_role
      // El currentUser puede tener user_type (de working_hours) o user_role (del store)
      // El servicio maneja ambas fuentes automáticamente
      return PermissionsService.canApproveAbsences(this.currentUser);
    },
    hasChanges() {
      return this.dayClocks.some(clock => clock.isModified || clock.isNew);
    },
    pendingAbsences() {
      if (!this.absences) return [];
      return this.absences.filter(absence => absence.status === 'pending');
    },
    nonHolidayAbsences() {
      if (!this.absences) return [];
      // Filtrar solo ausencias que NO sean vacaciones
      return this.absences.filter(absence => absence.tipoAusencia !== 'Vacaciones');
    },
    hasAbsences() {
      return this.nonHolidayAbsences && this.nonHolidayAbsences.length > 0;
    }
  },
  watch: {
    visible(newValue) {
      this.isVisible = newValue;
      if (newValue) {
        this.loadDayClocks();
      }
    },
    isVisible(newValue) {
      if (!newValue) {
        this.$emit('close');
      }
    }
  },
  methods: {
    showSnackbar(color, text) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.visible = true;
    },
    getAdminName(adminId) {
      const admin = this.users && this.users.find(user => user.id.toString() === adminId.toString());
      return admin ? admin.username : `Admin ${adminId}`;
    },

    loadDayClocks() {
      // Usar VueAdminPopup para cargar y preparar los clocks
      this.dayClocks = this.adminPopup.loadDayClocks(this.clocks);
    },

    async updateClockFromPopup(data) {
      if (!data || !data.updated_clock) {
        return;
      }
      try {
        const { updated_clock, justification } = data;

        await this.adminPopup.updateClock({
          uuid: updated_clock.uuid,
          timestamp: updated_clock.timestamp,
          type: updated_clock.type,
          user: updated_clock.user,
          justification: justification
        });
        await this.loadUserData();
      } catch (error) {
        this.showSnackbar('error', 'Error al actualizar el fichaje');
      }
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toTimeString().slice(0, 5);
    },

    formatDateTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleString('es-ES');
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    formatDateShort(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const options = {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      };
      return date.toLocaleDateString('es-ES', options).replace(/\./g, '');
    },

    getClockCardClass(clock) {
      if (this.hasPendingChanges(clock)) {
        return 'clock-card-pendiente';
      }
      return '';
    },

    getChangeCardClass(state) {
      return {
        'change-pendiente': state === 'pendiente' || state === 'pending',
        'change-validada': state === 'validada'
      };
    },

    getStateColor(state) {
      const colors = {
        initial: 'grey',
        pendiente: 'purple',
        pending: 'purple',
        validada: 'green'
      };
      return colors[state] || 'grey';
    },

    getStateText(state) {
      const texts = {
        initial: 'Fichaje Original',
        pendiente: 'Pendiente de Aprobación',
        pending: 'Pendiente de Aprobación',
        validada: 'Aprobada'
      };
      return texts[state] || state;
    },

    getStateIcon(state) {
      const icons = {
        initial: 'mdi-clock-outline',
        pendiente: 'mdi-alert-circle-outline',
        pending: 'mdi-alert-circle-outline',
        validada: 'mdi-check-circle'
      };
      return icons[state] || 'mdi-circle';
    },

    hasPendingChanges(clock) {
      // Usar la función de Utils
      return hasPendingChanges(clock);
    },

    async approveChange(clock, change, changeIndex) {
      this.approvingChange = true;
      try {
        // Usar VueAdminPopup para aprobar el cambio
        await this.adminPopup.approveChange(
          clock.uuid,
          changeIndex,
          this.currentUser.id
        );

        // Actualizar localmente para feedback inmediato
        change.state = 'validada';
        change.admin_uuid = this.currentUser.id.toString();
        change.fecha_aprobacion = new Date();

        // Emitir evento para recargar datos
        this.$emit('saved');
      } catch (error) {
        this.showSnackbar('error', 'Error al aprobar el cambio: ' + error.message);
      } finally {
        this.approvingChange = false;
      }
    },

    addNewClock() {
      if (!this.isAdmin) return;

      // Usar VueAdminPopup para crear el nuevo clock con la lógica de negocio
      const newClock = this.adminPopup.createNewClockForPopup(
        this.selectedUserId,
        this.selectedDate,
        this.dayClocks
      );

      this.dayClocks.push(newClock);
    },

    updateClockTime(clock) {
      if (!this.isAdmin) return;
      
      const [hours, minutes] = clock.timeString.split(':');
      const newDate = new Date(this.selectedDate);
      newDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      clock.timestamp = newDate;
      this.markAsModified(clock);
    },

    markAsModified(clock) {
      if (!this.isAdmin || clock.isNew) return;
      clock.isModified = true;
    },

  async saveChanges() {
    if (!this.isAdmin) return;

    this.isSaving = true;

    try {
      // Usar VueAdminPopup para guardar todos los cambios
      await this.adminPopup.saveClocks(this.dayClocks, this.selectedUserId);

      // Cerrar el popup primero para dar feedback inmediato
      this.closePopup();

      // Emitir el evento saved para que el componente padre recargue los datos
      this.$emit('saved');
    } catch (error) {
      this.showSnackbar('error', 'Error al guardar los cambios: ' + error.message);
    } finally {
      this.isSaving = false;
    }
  },

    closePopup() {
      this.isVisible = false;
      this.dayClocks = [];
    },

    formatAbsenceTime(dateStr) {
      const date = new Date(dateStr);
      return `${String(date.getHours()).padStart(2,'0')}:${String(date.getMinutes()).padStart(2,'0')}`;
    },

    getStatusText(status) {
      const texts = {
        pending: 'Pendiente',
        approved: 'Aprobada',
        rejected: 'Rechazada'
      };
      return texts[status] || status;
    },

    getStatusColor(status) {
      const colors = {
        pending: 'orange',
        approved: 'green',
        rejected: 'red'
      };
      return colors[status] || 'grey';
    },

    getAbsenceCardClass(status) {
      if (status === 'pending') {
        return 'absence-card-pending';
      } else if (status === 'approved') {
        return 'absence-card-approved';
      }
      return '';
    },

    async approveAbsence(absence) {
      try {
        const { sync_command_dispatcher } = await import('@/api/client');

        await sync_command_dispatcher.dispatch('approve_absence', {
          absence_uuid: absence.uuid,
          admin_user_id: this.currentUser.id,
          new_status: 'approved'
        });

        // Actualizar localmente el estado de la ausencia
        absence.status = 'approved';

        this.showSnackbar('success', 'Ausencia aprobada correctamente');

        // Emitir evento para recargar datos
        this.$emit('saved');

      } catch (error) {
        this.showSnackbar('error', 'Error al aprobar la ausencia: ' + error.message);
      }
    },

    async rejectAbsence(absence) {
      try {
        const { sync_command_dispatcher } = await import('@/api/client');

        await sync_command_dispatcher.dispatch('approve_absence', {
          absence_uuid: absence.uuid,
          admin_user_id: this.currentUser.id,
          new_status: 'rejected'
        });

        // Actualizar localmente el estado de la ausencia
        absence.status = 'rejected';

        this.showSnackbar('success', 'Ausencia rechazada correctamente');

        // Emitir evento para recargar datos
        this.$emit('saved');

      } catch (error) {
        this.showSnackbar('error', 'Error al rechazar la ausencia: ' + error.message);
      }
    },

    openJustificante(fotoJustificanteUrl) {
      if (!fotoJustificanteUrl) return;

      // Abrir el archivo en una nueva pestaña
      window.open(fotoJustificanteUrl, '_blank');
    },

  }
};
</script>

<style scoped>
.popup-header {
  background: linear-gradient(135deg, #7339B7 0%, #9C27B0 100%);
  color: white;
  padding: 20px 24px;
  box-shadow: 0 2px 8px rgba(115, 57, 183, 0.2);
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 600;
}

.header-date {
  font-size: 14px;
  opacity: 0.9;
  font-weight: 400;
  margin-left: 32px;
}

.popup-header .headline {
  color: white !important;
  font-size: 20px !important;
  font-weight: 600 !important;
}

.popup-header .v-icon {
  color: white !important;
}

.close-btn {
  margin-top: -8px;
}

.popup-content {
  padding: 40px;
  min-height: 500px;
  max-height: 75vh;
  overflow-y: auto;
  background-color: #fafafa;
}

.no-clocks-message {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.clock-item {
  margin-bottom: 28px;
}

.clock-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background: white;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}

.clock-card .v-card__text {
  padding: 0 !important;
}

.clock-card-pendiente {
  border-left: 4px solid #9C27B0 !important;
  background: white !important;
  box-shadow: 0 2px 4px rgba(156, 39, 176, 0.08) !important;
}

.changes-header {
  font-size: 14px;
  color: #666;
}

.change-item {
  margin-bottom: 12px;
}

.change-card {
  border-radius: 6px;
  margin: 8px 0;
  border: 1px solid #e0e0e0;
  background: white;
}

.change-pendiente {
  border-left: 4px solid #9C27B0 !important;
}

.change-validada {
  border-left: 4px solid #4CAF50 !important;
}

.change-rejected {
  border-left: 4px solid #F44336 !important;
}

.change-validating {
  border-left: 4px solid #FF9800 !important;
}

.change-info {
  font-size: 14px;
  line-height: 1.8;
}

.change-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.change-time-type {
  display: flex;
  align-items: center;
  font-size: 15px;
}

.change-time-type strong {
  color: #333;
  font-size: 16px;
}

.status-chip-large {
  font-weight: 500;
  font-size: 13px;
  height: 28px;
  padding: 0 12px;
}

.change-details {
  margin-bottom: 8px;
  font-size: 15px;
}

.change-justification {
  color: #666;
  font-style: italic;
  margin: 12px 0;
  padding: 10px 14px;
  background: var(--v-neutral-base);
  border-radius: 6px;
  border-left: 3px solid #e0e0e0;
}

.approval-info {
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding: 10px 14px;
  background: #f1f8f4;
  border-radius: 6px;
  border-left: 3px solid #4CAF50;
  font-size: 13px;
  color: #2e7d32;
}

.approval-info strong {
  color: #1b5e20;
}

.change-admin {
  color: #888;
  font-size: 13px;
  margin-top: 8px;
}

.admin-actions {
  border-top: 1px solid #eee;
  padding-top: 16px;
  margin-top: 16px;
}

.approve-change-btn {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.3px;
}

.clock-fields {
  margin-bottom: 16px;
}

.clock-fields .v-input {
  margin-bottom: 0;
}

.justification-row {
  margin-top: 12px;
  margin-bottom: 0;
}

.clock-actions {
  display: flex;
  align-items: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
}

.left-actions {
  display: flex;
  gap: 10px;
}

.add-clock-section {
  margin-top: 24px;
}

.add-clock-btn {
  border-style: dashed !important;
  border-width: 2px !important;
  border-color: #7339B7 !important;
  color: #7339B7 !important;
  font-weight: 500 !important;
  transition: all 0.2s ease;
}

.add-clock-btn:hover {
  background-color: rgba(115, 57, 183, 0.05) !important;
  border-color: #9C27B0 !important;
}

.popup-actions {
  padding: 20px 24px;
  background: linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%);
  border-top: 1px solid #e0e0e0;
}

/* Absence Section Styles */
.absences-section {
  margin-bottom: 40px;
  padding: 32px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 28px;
  padding: 14px 18px;
  background: linear-gradient(90deg, rgba(115, 57, 183, 0.05) 0%, rgba(156, 39, 176, 0.02) 100%);
  border-radius: 8px;
  border-left: 4px solid #7339B7;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #7339B7;
  letter-spacing: 0.5px;
}

.absence-card {
  margin-bottom: 24px;
}

.absence-card-content {
  padding: 24px !important;
}

.absence-card-pending {
  border-left: 4px solid #FF9800 !important;
  background: white !important;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04) !important;
  border: 1px solid #e0e0e0 !important;
  border-left: 4px solid #FF9800 !important;
  border-radius: 8px !important;
}

.absence-card-approved {
  border-left: 4px solid #4CAF50 !important;
  background: white !important;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04) !important;
  border: 1px solid #e0e0e0 !important;
  border-left: 4px solid #4CAF50 !important;
  border-radius: 8px !important;
}

.absence-info {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.absence-detail {
  font-size: 15px;
  color: #333;
  line-height: 1.6;
  display: flex;
  align-items: start;
  gap: 12px;
}

.absence-detail strong {
  min-width: 80px;
  color: #666;
  font-weight: 500;
  flex-shrink: 0;
}

.absence-detail span {
  flex: 1;
  color: #333;
}

.absence-actions-col {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
}

.absence-actions-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-end;
  width: 100%;
}

.clocks-section {
  margin-top: 32px;
  padding: 32px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

/* Admin action buttons */
.admin-action-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.action-btn {
  width: 100%;
  min-width: 140px;
  font-weight: 500;
}

.status-chip {
  min-width: 120px;
  height: 32px;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 600px) {
  .popup-content {
    padding: 16px;
  }

  .no-clocks-message {
    padding: 20px 10px;
  }

  .section-title {
    font-size: 16px;
  }

  .absence-detail {
    font-size: 13px;
  }

  .admin-action-buttons {
    flex-direction: column;
    width: 100%;
  }

  .admin-action-buttons .v-btn {
    width: 100%;
    margin: 0 0 8px 0 !important;
  }
}
</style>